'use client'

import type { ReactNode } from 'react'
import { RowSurfaceProvider } from '@/deskUiSkeletons/rowTemplates/RowSurfaceContext'
import type { RowSkin, RowSurface } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { SubRow } from '@/deskUiSkeletons/subs/SubRow'
import { showQuickReplyModal } from '@/deskUiSkeletons/Shared/QuickReplyModal'
import { rowVariantRegistry } from './RowTemplateRegistry'
import { nestedTemplateVariant } from './RowTemplateConfig'
import { defaultBlockedPreviewSkin } from '@/msgComponents/skins/blockedPreviewSkin'

const facebookRowSkin: RowSkin = {
  row: {
    base: 'min-w-0 list-none text-slate-900',
    hidden: 'hidden',
    checked: 'opacity-70',
    foreign: 'opacity-90',
    foreignFrom: 'block pt-0.5 text-xs text-slate-500 [&_a]:text-violet-700 [&_a]:no-underline [&_a:hover]:underline',
  },
  blockedNotice: { ...defaultBlockedPreviewSkin },
  icon: {
    base: 'inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border align-middle',
    checkOn: 'border-violet-600 bg-violet-600',
    checkOff: 'border-slate-300 bg-white',
    favOn: 'border-amber-400 bg-amber-300',
    favOff: 'border-slate-300 bg-white',
    qrOn: 'border-violet-600 bg-violet-100',
    qrOff: 'border-slate-300 bg-white',
  },
  titleLink: 'font-semibold text-slate-950 no-underline hover:text-violet-700',
  timeWrap: 'whitespace-nowrap text-xs text-slate-500',
  timeText: 'whitespace-nowrap text-xs text-slate-500',
  text: 'min-w-0 whitespace-normal text-sm leading-6 text-slate-700',
  metaLink: 'cursor-pointer text-xs font-medium text-violet-700',
  metaMore: 'cursor-pointer text-xs font-medium text-violet-700',
  meta: 'text-xs text-slate-500',
  feed: 'mt-3 flex list-none flex-col gap-3 p-0',
  folder: {
    row: 'flex flex-col gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2',
    head: 'flex min-w-0 items-center gap-2',
    title: 'min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm',
    time: 'flex-[0_0_auto] whitespace-nowrap text-xs text-slate-500',
    text: 'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-slate-600',
    counts: 'flex flex-wrap gap-x-3 text-xs text-slate-500',
    count: 'whitespace-nowrap',
  },
}

const facebookRowSurface: RowSurface = {
  SubRow,
  showQuickReplyModal,
  variants: rowVariantRegistry,
  config: { nestedTemplateVariant },
  skin: facebookRowSkin,
}

export function FacebookRowSurfaceProvider({ children }: { children: ReactNode }) {
  return <RowSurfaceProvider value={facebookRowSurface}>{children}</RowSurfaceProvider>
}
